import { DateTime } from 'luxon';
import { CreateEventFormData, EventParticipant } from '../components/types';
import {
  NylasCalendarEventDataFragment,
  CommonWhen,
  CommonWhenType,
  CommonWhenTimespan,
} from '../../../../../generated/graphql';
import { whenToCalendarEvent } from './whenHelpers';
import { formatDate, formatTime } from './dateTimeUtils';
import { getCurrentTimezone, getCanonicalTimezone } from './timezoneUtils';

export const hasDirtyFields = (
  dirtyFields: Record<string, any>,
  fields: string[],
): boolean => {
  return fields.some((field) => {
    const parts = field.split('.');
    let current = dirtyFields;

    for (const part of parts) {
      if (!current[part]) return false;
      current = current[part];
    }

    return true;
  });
};

export const getDefaultEventFormValues = (
  selectInfo: any | null,
  defaultStartDateTime = DateTime.now(),
): CreateEventFormData => {
  const startDateTime = selectInfo
    ? DateTime.fromJSDate(selectInfo.start)
    : defaultStartDateTime;

  const endDateTime = selectInfo
    ? DateTime.fromJSDate(selectInfo.end)
    : startDateTime.plus({ hours: 1 });

  const currentTimezone = getCanonicalTimezone(getCurrentTimezone());

  // Check if this is a single day click (start and end are on the same day)
  const isSingleDayClick =
    startDateTime.toFormat('yyyy-MM-dd') === endDateTime.toFormat('yyyy-MM-dd');

  // For all-day events, handle both single day clicks and multi-day drags
  if (selectInfo?.allDay) {
    const startDate = startDateTime.toFormat('yyyy-MM-dd');
    // For single day clicks, use the same date. For drags, subtract a day from the end date
    const endDate = isSingleDayClick
      ? startDate
      : endDateTime.minus({ days: 1 }).toFormat('yyyy-MM-dd');

    return {
      title: '',
      description: '',
      location: '',
      isAllDay: true,
      when: {
        startDate,
        endDate,
        startTime: '',
        endTime: '',
        startTimezone: currentTimezone,
        endTimezone: currentTimezone,
      },
      participants: [],
      resources: selectInfo?.resource?.extendedProps?.email
        ? [{ email: selectInfo.resource.extendedProps.email }]
        : [],
    };
  }

  // Convert times to the current timezone before rounding
  const startInCurrentTz = startDateTime.setZone(currentTimezone);
  const endInCurrentTz = endDateTime.setZone(currentTimezone);

  const roundedStartDateTime = startInCurrentTz.set({
    second: 0,
    millisecond: 0,
    minute: Math.round(startInCurrentTz.minute / 15) * 15,
  });

  const roundedEndDateTime = endInCurrentTz.set({
    second: 0,
    millisecond: 0,
    minute: Math.round(endInCurrentTz.minute / 15) * 15,
  });

  const resourceEmail = selectInfo?.resource?.extendedProps?.email;

  return {
    title: '',
    description: '',
    location: '',
    isAllDay: selectInfo?.allDay ?? false,
    when: {
      startDate: formatDate(
        roundedStartDateTime.toISO(),
        'yyyy-MM-dd',
        currentTimezone,
      ),
      startTime: formatTime(
        roundedStartDateTime.toISO(),
        'HH:mm',
        currentTimezone,
      ),
      endDate: formatDate(
        roundedEndDateTime.toISO(),
        'yyyy-MM-dd',
        currentTimezone,
      ),
      endTime: formatTime(roundedEndDateTime.toISO(), 'HH:mm', currentTimezone),
      startTimezone: currentTimezone,
      endTimezone: currentTimezone,
    },
    participants: [],
    resources: resourceEmail ? [{ email: resourceEmail }] : [],
  };
};

export const getEditEventFormValues = (
  event: NylasCalendarEventDataFragment | null,
): CreateEventFormData => {
  if (!event) {
    return getDefaultEventFormValues(null);
  }

  const calendarEvent = whenToCalendarEvent(event.when as CommonWhen, {});
  if (!calendarEvent) {
    return getDefaultEventFormValues(null);
  }

  const isAllDay = calendarEvent.allDay;
  const isTimespan = event.when.type === CommonWhenType.Timespan;
  const startTimezone = isTimespan
    ? (event.when as CommonWhenTimespan).startTimezone
    : getCurrentTimezone();
  const endTimezone = isTimespan
    ? (event.when as CommonWhenTimespan).endTimezone
    : getCurrentTimezone();

  const participants =
    event.participants?.map((p) => ({
      email: p.email,
      name: p.name || '',
      status: (p.status as EventParticipant['status']) || 'noreply',
    })) || [];

  const resources =
    event.resources?.map((r) => ({
      email: r.email,
    })) || [];

  return {
    title: event.title || '',
    description: event.description || '',
    location: event.location || '',
    isAllDay,
    when: {
      startDate: formatDate(calendarEvent.start, 'yyyy-MM-dd', startTimezone),
      startTime: isAllDay
        ? ''
        : formatTime(calendarEvent.start, 'HH:mm', startTimezone),
      endDate: formatDate(calendarEvent.end, 'yyyy-MM-dd', endTimezone),
      endTime: isAllDay
        ? ''
        : formatTime(calendarEvent.end, 'HH:mm', endTimezone),
      startTimezone,
      endTimezone,
    },
    participants,
    resources,
  };
};
